import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  Login as LoginIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  PhotoCamera as PhotoCameraIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import nativeCameraService from '../../services/nativeCameraService';
import { io, Socket } from 'socket.io-client';
import PWAInstall from '../../components/PWAInstall';
import MemberSearch from '../../components/MemberSearch/MemberSearch';
import { Member } from '../../types';

// Unique identifier for this tablet device
const DEVICE_ID = 'TABLET_' + Math.random().toString(36).substring(2, 10);

const TabletSignIn: React.FC = () => {
  const [idNumber, setIdNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [response, setResponse] = useState<{
    success: boolean;
    message: string;
    member?: {
      _id: string;
      idNumber: string; // Changed from memberId to idNumber
      firstName: string;
      lastName: string;
      fullName: string;
      membershipType: string;
      membershipStatus: string;
      paymentStatus: string;
      startDate: string;
      endDate: string;
      age: number;
      daysRemaining: number;
      profileImage?: string;
      signedInAs?: {
        name: string;
        relationship: string;
        isPrimaryMember: boolean;
      };
    };
    validation?: {
      isValid: boolean;
      status: string;
      message: string;
    };
  } | null>(null);

  // Photo capture states
  const [photoRequest, setPhotoRequest] = useState<{
    requestId: string;
    memberId: string; // This is the MongoDB _id, not the ID number
    memberName: string;
    requestedBy: string;
    timestamp: string;
  } | null>(null);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [photoCapturing, setPhotoCapturing] = useState(false);
  const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const socketRef = useRef<Socket | null>(null);

  // Camera support detection states
  const handleResponseClose = useCallback(() => {
    setResponse(null);
    setIdNumber('');
    setSelectedMember(null);
  }, []);

  // Clear response after 10 seconds
  useEffect(() => {
    if (!response) {
      return;
    }

    const timer = setTimeout(() => {
      handleResponseClose();
    }, 10000);

    return () => clearTimeout(timer);
  }, [response, handleResponseClose]);

  // Socket.IO connection for photo requests
  useEffect(() => {
    // Connect to Socket.IO server
    // Prefer explicit socket URL, then API URL; otherwise use relative (same-origin via nginx)
    const rawSocket = (process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || '').trim();
    let socketUrl: string = rawSocket;

    try {
      if (!socketUrl) {
        socketUrl = '';
      } else if (!/^https?:\/\//i.test(socketUrl)) {
        // Add protocol if missing to allow URL parsing below
        socketUrl = (typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'https://' : 'http://') + socketUrl;
      }

      // In production over HTTPS, avoid forcing same-host non-standard ports or http protocol
      if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production' && window.location.protocol === 'https:' && socketUrl) {
        const u = new URL(socketUrl);
        const sameHost = u.hostname === window.location.hostname;
        if (sameHost && ((u.port && u.port !== '' && u.port !== '443') || u.protocol === 'http:')) {
          console.warn('[TabletSignIn] Overriding socket URL to same-origin for HTTPS production context:', socketUrl);
          socketUrl = '';
        }
      }
    } catch (e) {
      console.warn('[TabletSignIn] Failed to normalize socket URL, defaulting to same-origin. Raw:', rawSocket, e);
      socketUrl = '';
    }

    const socket = io(socketUrl);
    socketRef.current = socket;

    console.log('📱 Tablet connected to socket server');

    // Listen for photo requests
    socket.on('photo:request', (data) => {
      console.log('📷 Photo request received:', data);
      setPhotoRequest(data);
      setShowPhotoDialog(true);
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Check browser and camera support on component mount
  useEffect(() => {
    checkBrowserAndCameraSupport();
  }, []);

  const checkBrowserAndCameraSupport = () => {
    const userAgent = navigator.userAgent;
    const isHTTPS = window.location.protocol === 'https:';

    // Detect browser
    let browserName = 'Unknown';
    let browserVersion = 'Unknown';

    if (userAgent.indexOf('Chrome') > -1) {
      browserName = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.indexOf('Firefox') > -1) {
      browserName = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.indexOf('Safari') > -1 && userAgent.indexOf('Chrome') === -1) {
      browserName = 'Safari';
      const match = userAgent.match(/Version\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    } else if (userAgent.indexOf('Edge') > -1) {
      browserName = 'Edge';
      const match = userAgent.match(/Edge\/(\d+)/);
      browserVersion = match ? match[1] : 'Unknown';
    }

    // Check platform
    let platform = 'Unknown';
    if (userAgent.indexOf('Android') > -1) platform = 'Android';
    else if (userAgent.indexOf('iPad') > -1 || userAgent.indexOf('iPhone') > -1) platform = 'iOS';
    else if (userAgent.indexOf('Windows') > -1) platform = 'Windows';
    else if (userAgent.indexOf('Mac') > -1) platform = 'macOS';
    else if (userAgent.indexOf('Linux') > -1) platform = 'Linux';

    // Check MediaDevices API support
    const hasMediaDevices = !!(navigator.mediaDevices);
    const hasGetUserMedia = !!(navigator.mediaDevices?.getUserMedia);

    const info = {
      name: browserName,
      version: browserVersion,
      platform,
      userAgent,
      isHTTPS,
      hasMediaDevices,
      hasGetUserMedia
    };

    // Determine camera support
    const supported = hasMediaDevices && hasGetUserMedia && (isHTTPS || window.location.hostname === 'localhost');

    console.log('🔍 Browser Detection:', info);
    console.log('📷 Camera Supported:', supported);

    if (!supported) {
      let reason = 'Unknown reason';
      if (!hasMediaDevices) reason = 'MediaDevices API not supported';
      else if (!hasGetUserMedia) reason = 'getUserMedia not supported';
      else if (!isHTTPS && window.location.hostname !== 'localhost') reason = 'HTTPS required for camera access';

      console.error('❌ Camera not supported:', reason);
      setNotification({
        message: `Camera not supported: ${reason}`,
        severity: 'error'
      });
    }
  };

  const handlePhotoAccept = async () => {
    if (!photoRequest) return;

    try {
      setPhotoCapturing(true);
      setShowPhotoDialog(false);

      // Emit photo request accepted
      socketRef.current?.emit('photo:request-accepted', {
        requestId: photoRequest.requestId,
        memberId: photoRequest.memberId,
        memberName: photoRequest.memberName,
      });

      // Check if camera is available before requesting access
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported by this browser');
      }

      console.log('📷 Requesting camera access...');

      // Check current permissions
      if (navigator.permissions) {
        try {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
          console.log('📷 Camera permission status:', cameraPermission.state);

          if (cameraPermission.state === 'denied') {
            throw new Error('Camera permission denied. Please enable camera access in browser settings.');
          }
        } catch (permError) {
          console.warn('📷 Could not check camera permissions:', permError);
        }
      }

      // Try different camera constraints for better compatibility
      let stream: MediaStream | null = null;
      const constraints = [
        // Try user-facing camera first
        { video: { facingMode: 'user' }, audio: false },
        // Fallback to any camera
        { video: true, audio: false },
        // Try with specific resolution for tablet compatibility
        {
          video: {
            facingMode: 'user',
            width: { ideal: 640 },
            height: { ideal: 480 }
          },
          audio: false
        },
        // Last resort - basic video
        { video: { facingMode: { exact: 'user' } }, audio: false }
      ];

      for (let i = 0; i < constraints.length; i++) {
        try {
          console.log(`📷 Trying camera constraint ${i + 1}:`, constraints[i]);
          stream = await navigator.mediaDevices.getUserMedia(constraints[i]);
          console.log('📷 Camera access successful with constraint:', i + 1);
          break;
        } catch (constraintError) {
          console.warn(`📷 Camera constraint ${i + 1} failed:`, constraintError);
          if (i === constraints.length - 1) {
            throw constraintError;
          }
        }
      }

      if (!stream) {
        throw new Error('Could not access camera with any configuration');
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        // Handle video load events
        videoRef.current.onloadedmetadata = () => {
          console.log('📷 Video metadata loaded');
        };

        videoRef.current.oncanplay = () => {
          console.log('📷 Video can play');
        };

        try {
          await videoRef.current.play();
          console.log('📷 Video playing successfully');
        } catch (playError) {
          console.error('📷 Video play error:', playError);
          throw new Error('Could not start video playback');
        }
      }

      // Auto-capture photo after 3 seconds
      setTimeout(() => {
        capturePhoto();
      }, 3000);

    } catch (error: any) {
      console.error('📷 Camera access error:', error);

      let errorMessage = 'Could not access camera. ';

      if (error.name === 'NotAllowedError') {
        errorMessage += 'Camera permission denied. Please:\n1. Check browser settings\n2. Allow camera access for this site\n3. Refresh the page and try again';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No camera found on device.';
      } else if (error.name === 'NotReadableError') {
        errorMessage += 'Camera is being used by another application.';
      } else if (error.name === 'OverconstrainedError') {
        errorMessage += 'Camera does not support the requested configuration.';
      } else if (error.name === 'SecurityError') {
        errorMessage += 'Camera access blocked by security policy. Make sure you\'re using HTTPS or localhost.';
      } else {
        errorMessage += error.message || 'Unknown camera error.';
      }

      setNotification({
        message: errorMessage,
        severity: 'error'
      });
      setPhotoCapturing(false);
      setPhotoRequest(null);
    }
  };

  const handlePhotoDecline = () => {
    if (!photoRequest) return;

    // Emit photo request declined
    socketRef.current?.emit('photo:request-declined', {
      requestId: photoRequest.requestId,
      memberId: photoRequest.memberId,
      memberName: photoRequest.memberName,
    });

    setShowPhotoDialog(false);
    setPhotoRequest(null);
    setNotification({
      message: 'Photo request declined.',
      severity: 'info'
    });
  };

  const capturePhoto = async () => {
    if (!photoRequest) return;

    try {
      setPhotoCapturing(true);

      // Use native camera service if available, otherwise fall back to web camera
      if (nativeCameraService.isNativeApp && nativeCameraService.hasCamera) {
        console.log('[TabletSignIn] Using native camera service');

        const result = await nativeCameraService.takePhoto();

        if (result.success && result.photo) {
          // Convert Capacitor Photo to File for upload
          let file: File;

          // Check if it's a Photo (from native) or File (from web)
          if ('dataUrl' in result.photo && result.photo.dataUrl) {
            // It's a Capacitor Photo with dataUrl
            const response = await fetch(result.photo.dataUrl);
            const blob = await response.blob();
            file = new File([blob], `photo-${photoRequest.memberId}-${Date.now()}.jpg`, {
              type: 'image/jpeg',
            });
          } else if (result.photo instanceof File) {
            // It's already a File
            file = result.photo;
          } else {
            throw new Error('Invalid photo format received from camera');
          }

          // Upload photo to server
          const uploadResponse = await apiService.uploadMemberPhoto(photoRequest.memberId, file);

          // Emit photo captured and upload complete events
          socketRef.current?.emit('photo:captured', {
            requestId: photoRequest.requestId,
            memberId: photoRequest.memberId,
            memberName: photoRequest.memberName,
          });

          socketRef.current?.emit('photo:upload-complete', {
            requestId: photoRequest.requestId,
            memberId: photoRequest.memberId,
            memberName: photoRequest.memberName,
            profileImage: uploadResponse.profileImage,
          });

          setNotification({
            message: `Photo captured successfully for ${photoRequest.memberName}!`,
            severity: 'success'
          });

        } else {
          throw new Error(result.error || 'Native camera capture failed');
        }

      } else {
        // Fall back to web camera (existing implementation)
        console.log('[TabletSignIn] Using web camera fallback');

        if (!videoRef.current || !canvasRef.current) {
          throw new Error('Camera video not available');
        }

        const canvas = canvasRef.current;
        const video = videoRef.current;
        const context = canvas.getContext('2d');

        if (!context) throw new Error('Canvas context not available');

        // Set canvas size to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw video frame to canvas
        context.drawImage(video, 0, 0);

        // Convert canvas to blob
        await new Promise<void>((resolve, reject) => {
          canvas.toBlob(async (blob) => {
            if (!blob) {
              reject(new Error('Failed to create image blob'));
              return;
            }

            try {
              // Convert blob to File object
              const file = new File([blob], `photo-${photoRequest.memberId}-${Date.now()}.jpg`, {
                type: 'image/jpeg',
              });

              // Upload photo to server
              const response = await apiService.uploadMemberPhoto(photoRequest.memberId, file);

              // Emit photo captured and upload complete events
              socketRef.current?.emit('photo:captured', {
                requestId: photoRequest.requestId,
                memberId: photoRequest.memberId,
                memberName: photoRequest.memberName,
              });

              socketRef.current?.emit('photo:upload-complete', {
                requestId: photoRequest.requestId,
                memberId: photoRequest.memberId,
                memberName: photoRequest.memberName,
                profileImage: response.profileImage,
              });

              setNotification({
                message: `Photo captured successfully for ${photoRequest.memberName}!`,
                severity: 'success'
              });

              resolve();
            } catch (error) {
              reject(error);
            }
          }, 'image/jpeg', 0.8);
        });
      }

    } catch (error) {
      console.error('Error capturing photo:', error);
      setNotification({
        message: `Failed to capture photo: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      // Stop camera and cleanup
      stopCamera();
      setPhotoCapturing(false);
      setPhotoRequest(null);
      setShowPhotoDialog(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const handleNotificationClose = () => {
    setNotification(null);
  };

  const attemptSignIn = async (rawIdNumber: string) => {
    if (loading) {
      return;
    }

    const trimmedId = rawIdNumber.trim();

    if (!trimmedId) {
      setResponse({
        success: false,
        message: 'Please enter or select a member before signing in.',
      });
      return;
    }

    setLoading(true);
    console.log('[TabletSignIn] Attempting check-in for ID:', trimmedId);

    try {
      const result = await apiService.checkInMember({
        idNumber: trimmedId,
        deviceId: DEVICE_ID,
        deviceType: 'tablet',
      });

      console.log('[TabletSignIn] Check-in successful:', result);

      setResponse({
        success: result.success,
        message: result.validation.message,
        member: result.member,
        validation: result.validation,
      });
    } catch (error: any) {
      console.error('[TabletSignIn] Check-in failed:', error);
      console.error('[TabletSignIn] Error response:', error.response?.data);

      setResponse({
        success: false,
        message: error.response?.data?.message || 'Member ID not found. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await attemptSignIn(idNumber);
  };

  const handleMemberSelected = (member: Member | null) => {
    try {
      setSelectedMember(member);

      if (!member) {
        setIdNumber('');
        return;
      }

      if (!member.idNumber) {
        setIdNumber('');
        setResponse({
          success: false,
          message: 'Selected member does not have an ID number on file. Please contact the front desk.',
        });
        return;
      }

      setIdNumber(member.idNumber);
      attemptSignIn(member.idNumber);
    } catch (error) {
      console.error('Error in handleMemberSelected:', error);
      setResponse({
        success: false,
        message: 'An error occurred while processing the member selection.',
      });
    }
  };


  const getStatusIcon = () => {
    if (!response) return null;

    if (!response.success) {
      return <ErrorIcon sx={{ fontSize: 80, color: 'error.main' }} />;
    }

    if (response.validation?.status === 'success') {
      return <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />;
    }

    return <WarningIcon sx={{ fontSize: 80, color: 'warning.main' }} />;
  };

  const getStatusColor = () => {
    if (!response) return 'primary';

    if (!response.success) {
      return 'error';
    }

    if (response.validation?.status === 'success') {
      return 'success';
    }

    return 'warning';
  };

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      p: 2
    }}>
      <Paper elevation={3} sx={{ width: '100%', maxWidth: 600, mb: 4 }}>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          {/* PWA Install Component */}
          <PWAInstall onInstallSuccess={() => setNotification({ message: 'Tablet app installed!', severity: 'success' })} />

          <Typography variant="h2" component="h2" gutterBottom>
            Legends Lifestyle
            <br />
            Member Sign-In
          </Typography>
          <Box
            component="img"
            src="/static/media/Legends-logo.ab066bd1cedcef5df982.jpg"
            alt="Legends Gym Logo"
            sx={{ width: 200, height: 'auto', margin: '20px auto', display: 'block' }}
          />

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 4 }}
          >
            <MemberSearch
              value={selectedMember}
              onChange={handleMemberSelected}
              label="Member Info"
              placeholder="Search by name, email, or ID number..."
              helperText="Select a member to automatically sign them in."
              disabled={loading}
              usePublicSearch={true}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={loading || !idNumber.trim()}
              startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <LoginIcon />}
              sx={{ py: 2, fontSize: 20 }}
            >
              {loading ? 'Processing...' : 'Sign In'}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Search for a member by name, email, or ID number. Selecting automatically signs them in.
          </Typography>
        </Box>
      </Paper>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        Need help? Please contact the front desk.
      </Typography>

      <Dialog
        open={Boolean(response)}
        onClose={handleResponseClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1, color: response ? `${getStatusColor()}.main` : 'primary.main' }}>
          {response?.success ? 'Sign-In Successful' : 'Sign-In Update'}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          {response && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {getStatusIcon()}
              </Box>

              <Typography variant="h5" component="p" sx={{ mb: 2 }}>
                {response.message}
              </Typography>

              {response.member?.signedInAs && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: 'primary.contrastText', mb: 1 }}>
                    Signed in as: {response.member.signedInAs.name}
                  </Typography>
                  <Chip
                    label={response.member.signedInAs.relationship}
                    color={response.member.signedInAs.isPrimaryMember ? 'primary' : 'secondary'}
                    sx={{ color: 'primary.contrastText' }}
                  />
                </Box>
              )}

              {response.member && (
                <Box sx={{ mt: 3, textAlign: 'left' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Membership Details
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Primary Member:</strong> {response.member.fullName}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Membership Type:</strong> {response.member.membershipType}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Status:</strong> {response.member.membershipStatus}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Payment:</strong> {response.member.paymentStatus}
                  </Typography>
                  {response.member.daysRemaining !== undefined && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Days Remaining:</strong> {response.member.daysRemaining}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Chip label={response.member.membershipType} color="primary" variant="outlined" />
                    <Chip
                      label={`Status: ${response.member.membershipStatus}`}
                      color={response.member.membershipStatus === 'active' ? 'success' : 'warning'}
                      variant="outlined"
                    />
                    <Chip
                      label={`Payment: ${response.member.paymentStatus}`}
                      color={response.member.paymentStatus === 'current' ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </Box>
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
                This message will close automatically in 10 seconds.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleResponseClose}
            variant="contained"
            color={response?.success ? 'success' : 'primary'}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Request Dialog */}
      <Dialog
        open={showPhotoDialog}
        onClose={handlePhotoDecline}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          📷 Photo Request
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            An admin has requested to take your photo for your membership profile.
          </Typography>
          {photoRequest && (
            <Typography variant="h6" sx={{ textAlign: 'center', color: 'primary.main' }}>
              {photoRequest.memberName}
            </Typography>
          )}
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
            Do you agree to have your photo taken?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handlePhotoDecline}
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            sx={{ mr: 2 }}
          >
            No, Decline
          </Button>
          <Button
            onClick={handlePhotoAccept}
            variant="contained"
            color="primary"
            startIcon={<PhotoCameraIcon />}
          >
            Yes, Take Photo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Capturing Overlay */}
      {photoCapturing && (
        <Dialog
          open={photoCapturing}
          fullScreen
          PaperProps={{
            sx: { bgcolor: 'black', display: 'flex', flexDirection: 'column' }
          }}
        >
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}>
            {nativeCameraService.isNativeApp && nativeCameraService.hasCamera ? (
              // Native camera interface - no video preview needed
              <>
                <PhotoCameraIcon sx={{ fontSize: 120, color: 'white', mb: 3 }} />
                <Typography variant="h4" sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
                  📷 Native Camera
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
                  Camera will open automatically. Follow the prompts to take your photo.
                </Typography>
                <CircularProgress sx={{ mt: 3, color: 'white' }} size={60} />
              </>
            ) : (
              // Web camera interface - show video preview
              <>
                <Typography variant="h4" sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
                  📷 Taking Photo...
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
                  Please look at the camera. Photo will be taken automatically.
                </Typography>

                <Box
                  component="video"
                  ref={videoRef}
                  sx={{
                    width: '100%',
                    maxWidth: '500px',
                    height: 'auto',
                    borderRadius: '10px',
                    border: '3px solid white'
                  }}
                  playsInline
                  muted
                />

                <Box
                  component="canvas"
                  ref={canvasRef}
                  sx={{ display: 'none' }}
                />

                <CircularProgress sx={{ mt: 3, color: 'white' }} size={60} />
              </>
            )}
          </Box>
        </Dialog>
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {notification?.message || ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TabletSignIn;
